import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaRegEdit, FaRegTrashAlt, FaPlus, FaSearch } from "react-icons/fa";
import PointRulesDialog from "./PointRulesDialog";
import { API_URL } from "../../../../api";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
} from "@mui/material";

const PointRules = () => {
  const token = localStorage.getItem("token");
  const url = API_URL();
  const api = axios.create({
    baseURL: url,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const [pointRules, setPointRules] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const showDialog = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleEdit = (data) => {
    setEditData(data);
    setOpen(true);
  };

  const handleDelete = (id) => {
    api
      .delete("point-rules/" + id)
      .then(() => {
        Swal.fire({
          title: "Success",
          text: "Successfully Deleted!",
          icon: "success",
          confirmButtonText: "OK",
        });
        getPointRules();
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          text: error?.response?.data?.message || "Error deleting point rule",
          icon: "error",
          confirmButtonText: "CANCEL",
        });
      });
  };

  const confirmDelete = (id) => {
    Swal.fire({
      title: "Delete Confirmation",
      text: "Are you sure to delete this point rule?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(id);
      }
    });
  };

  const getPointRules = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("point-rules", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
        },
      });
      const dataList = res.data?.data || [];
      setPointRules(dataList);
      setTotalCount(res.data?._metadata?.total || dataList.length);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getPointRules();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Type badge color helper
  const getTypeColor = (type) => {
    switch (type) {
      case "Product": return "primary";
      case "Rental":  return "secondary";
      case "Service": return "success";
      case "Package": return "warning";
      default:        return "default";
    }
  };

  return (
    <div className="App">
      <div className="wrapper">
        <div className="content-header pb-2">
          <div className="container-fluid px-3">
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: "#333", display: "flex", alignItems: "center", gap: 1 }}
            >
              Point Rules Management
            </Typography>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid px-3">
            <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                {/* Toolbar */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search by shop name..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0);
                    }}
                    sx={{ width: { xs: "100%", sm: "300px" } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaSearch color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FaPlus />}
                    onClick={showDialog}
                    sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                  >
                    Create Point Rule
                  </Button>
                </Box>

                {/* Table */}
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow
                        sx={{ "& th": { backgroundColor: "#f5f5f5", color: "#333", fontWeight: "bold" } }}
                      >
                        <TableCell>#</TableCell>
                        <TableCell>Points</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Currency</TableCell>
                        <TableCell>Related Shop</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: "center", py: 5 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : pointRules.length > 0 ? (
                        pointRules.map((rule, i) => {
                          const shopName =
                            rule.relatedShop?.name ||
                            rule.relatedShop?.shopName ||
                            (typeof rule.relatedShop === "string" ? rule.relatedShop : null);

                          return (
                            <TableRow key={rule._id || i} hover>
                              <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                              <TableCell>{rule.point ?? "-"}</TableCell>
                              <TableCell>{rule.amount ?? "-"}</TableCell>
                              <TableCell>
                                {rule.type ? (
                                  <Chip
                                    label={rule.type}
                                    color={getTypeColor(rule.type)}
                                    size="small"
                                    variant="outlined"
                                  />
                                ) : "-"}
                              </TableCell>
                              <TableCell>{rule.currency || "-"}</TableCell>
                              <TableCell>{shopName || "-"}</TableCell>
                              <TableCell
                                sx={{
                                  maxWidth: 200,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {rule.description || "-"}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Edit">
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleEdit(rule)}
                                    size="small"
                                  >
                                    <FaRegEdit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    color="error"
                                    onClick={() => confirmDelete(rule._id)}
                                    size="small"
                                  >
                                    <FaRegTrashAlt />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ textAlign: "center", py: 5, color: "text.secondary" }}
                          >
                            No point rules found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ "& p": { marginBottom: 0 } }}
                  />
                </TableContainer>
              </CardContent>
            </Card>

            <PointRulesDialog
              open={open}
              close={() => setOpen(false)}
              getPointRules={getPointRules}
              editData={editData}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default PointRules;